import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Input, Tag } from 'antd'
import { ArrowLeftOutlined, CaretRightOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { createStrategyVersion, getStrategyVersion, submitStrategyVersion, updateStrategyVersion } from '@/api'
import type { CreateEditStrategyVersion } from '@/types'
import { sectorOptions } from '@/pages/static'
import { useFeedback } from '@/utils/antdApp'
import './index.scss'

type CreateVersionPageProps = {
    versionId?: number | null
    managerId?: number | null
    color?: string | null
    onBack?: () => void
    onSubmitted?: (action: 'draft' | 'submit', nextVersionId?: number) => void
    value?: Partial<CreateEditStrategyVersion>
    updateValue?: (nextValue: Partial<CreateEditStrategyVersion>) => void
}

export default function CreateVersionPage({
    versionId: controlledVersionId,
    managerId: controlledManagerId,
    color: controlledColor,
    onBack,
    onSubmitted,
    value,
    updateValue,
}: CreateVersionPageProps = {}) {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const params = useParams()
    const [searchParams] = useSearchParams()
    const [formData, setFormData] = useState<CreateEditStrategyVersion>({
        name: '',
        market: 'HK',
        strategy_snapshot: '',
        constraints: [],
        focus_sectors: [],
        ...value,
    })
    const routeVersionId = params.id ? Number(params.id) : null
    const currentVersionId = controlledVersionId ?? routeVersionId
    const initialManagerId = searchParams.get('manager_id') ? Number(searchParams.get('manager_id')) : null
    const color = controlledColor ?? searchParams.get('color')
    const accentColor = color ? decodeURIComponent(color) : undefined
    const [managerId, setManagerId] = useState<number | null>(controlledManagerId ?? initialManagerId)
    const [, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [versionStatus, setVersionStatus] = useState<string | null>(currentVersionId ? null : 'draft')
    const title = useMemo(() => currentVersionId ? '编辑版本' : '新草稿', [currentVersionId])
    const isInPlaceEdit = versionStatus === 'active' || versionStatus === 'archived'
    const statusLabel = versionStatus === 'active'
        ? '已激活'
        : versionStatus === 'archived'
            ? '已归档'
            : '草稿'

    const syncFormData = (next: CreateEditStrategyVersion) => {
        setFormData(next)
        updateValue?.(next)
    }

    useEffect(() => {
        if (controlledManagerId != null) {
            setManagerId(controlledManagerId)
        }

        if (currentVersionId != null) {
            getStrategyVersion(currentVersionId)
                .then((data) => {
                    const nextFormData = {
                        name: data.name,
                        market: data.market as 'HK' | 'US',
                        strategy_snapshot: data.strategy_snapshot,
                        constraints: data.constraints,
                        focus_sectors: data.focus_sectors,
                    }
                    setVersionStatus(data.status)
                    setManagerId(data.manager_id)
                    setFormData(nextFormData)
                    updateValue?.(nextFormData)
                })
                .catch((error) => {
                    console.error(error)
                    message.error('获取策略版本失败')
                })
                .finally(() => {
                    setLoading(false)
                })
            return
        }

        setVersionStatus('draft')
        setLoading(false)
    }, [currentVersionId, controlledManagerId])

    useEffect(() => {
        if (value) {
            setFormData((prev) => ({
                ...prev,
                ...value,
            }))
        }
    }, [value])

    const updateFormData = <K extends keyof CreateEditStrategyVersion>(key: K, value: CreateEditStrategyVersion[K]) => {
        syncFormData({ ...formData, [key]: value })
    }

    const handleBack = () => {
        if (onBack) {
            onBack()
            return
        }
        if (managerId != null && !Number.isNaN(managerId)) {
            const nextParams = new URLSearchParams({ manager_id: String(managerId) })
            if (color) {
                nextParams.set('color', color)
            }
            navigate(`/versionManage?${nextParams.toString()}`)
            return
        }
        navigate('/versionManage')
    }

    const handleRunValidation = async () => {
        try {
             //版本策略id
             const { id } = currentVersionId ? await updateStrategyVersion(currentVersionId, formData) : await createStrategyVersion({
                manager_id: managerId,
            ...formData}) 

            if(id){
                sessionStorage.setItem('STRAGY_VERSION_MODE', JSON.stringify({managerId: managerId, versionId: id}))
                navigate(`/backtests/backtest?manager_id=${managerId}&version_id=${id}`)
            }
        } catch (error) {
            if (error instanceof Error && error.message) {
                message.error(error.message)
            }
        }
    }

    const toggleSector = (sector: string, checked: boolean) => {
        syncFormData({
            ...formData,
            focus_sectors: checked
                ? [...formData.focus_sectors, sector]
                : formData.focus_sectors.filter((item) => item !== sector),
        })
    }

    const updateConstraintDraft = (value: string, index: number) => {
        syncFormData({
            ...formData,
            constraints: formData.constraints.map((item, itemIndex) => itemIndex === index ? value : item),
        })
    }

    const changeConstraint = (value: string , index: number) => {
        const val = value.trim()
        syncFormData({
            ...formData,
            constraints: (() => {
                if (!val) {
                    return formData.constraints.filter((_, itemIndex) => itemIndex !== index)
                }

                if (formData.constraints.some((item, itemIndex) => itemIndex !== index && item.trim() === val)) {
                    message.warning('该约束已存在')
                    return formData.constraints.map((item, itemIndex) => itemIndex === index ? '' : item)
                }

                return formData.constraints.map((item, itemIndex) => itemIndex === index ? val : item)
            })(),
        })
    }

    const addConstraint = () => {
        syncFormData({
            ...formData,
            constraints: [...formData.constraints, ''],
        })
    }

    const removeConstraint = (index: number) => {
        syncFormData({
            ...formData,
            constraints: formData.constraints.filter((_, itemIndex) => itemIndex !== index),
        })
        console.log(formData)
    }

    const handleSubmit = async (action: 'draft' | 'submit') => {

        try {
            setSubmitting(true)

            //版本策略id
            const { id } = currentVersionId ? await updateStrategyVersion(currentVersionId, formData) : await createStrategyVersion({
                manager_id: managerId,
            ...formData}) 

            if(id){
                message.success(isInPlaceEdit ? '版本已保存' : action === 'draft' ? '草稿已保存' : '版本已提交审批')
                if(action === 'submit' && !isInPlaceEdit){
                    submitStrategyVersion(id).then((res) => {
                        if(res){
                            onSubmitted?.(action, id)
                        }
                    })
                } else{
                    onSubmitted?.(action, id)
                } 
            }
        } catch (error) {
            if (error instanceof Error && error.message) {
                message.error(error.message)
            }
        } finally {
            setSubmitting(false)
        }
    }

    const content = (
        <div className="create-version-page">
            <div className="page-header create-version-page__header">
                <div className="create-version-page__header-left">
                    <Button
                        type="text"
                        className="create-version-page__back"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                    >
                        版本列表
                    </Button>
                    <span>/</span>
                    <span className="create-version-page__title">
                        <b style={{ color: accentColor }}>·</b> {title}
                    </span>
                    <Tag className={`text-10 is-round ${versionStatus === 'active' ? 'is-success' : 'color-2'}`}>{statusLabel}</Tag>
                    <Tag className="text-10 is-purple">{formData.market}</Tag>
                </div>

                <Button
                    type="primary"
                    icon={<CaretRightOutlined />}
                    disabled={submitting}
                    onClick={handleRunValidation}
                >
                    运行回测验证
                </Button>
            </div>

            <div className="page-content create-version-page__content">
                <div className="create-version-page__body">
                    <Alert
                        showIcon={false}
                        type="info"
                        className="create-version-page__notice"
                        description={isInPlaceEdit
                            ? (versionStatus === 'archived'
                                ? '正在直接修改已归档版本。保存后状态保持归档，不会重新进入审批。'
                                : '正在直接修改已激活版本。保存后仍然是当前激活版本。')
                            : '✏️ 草稿模式：直接编辑参数，保存后可运行回测验证，满意后提交审批'}
                    />
                    <section className="create-version-page__section grid">
                        <div className="create-version-page__section-title">版本信息</div>
                        <div className='create-version-page__form-item flex items-center'>
                            <span className='create-version-page__form-item-label text-12'>版本名称</span>
                            <Input 
                                className='ant-edit-input'
                                placeholder="请输入版本名称" maxLength={24} 
                                value={formData.name}
                                onChange={(e) => updateFormData('name', e.target.value)}
                            />
                        </div>
                        <div className='create-version-page__form-item flex items-center'>
                            <span className='create-version-page__form-item-label text-12'>市场</span>
                            <Tag 
                                className={`stock-market-tag ${formData.market === 'HK' ? 'is-active' : ''}`} 
                                onClick={() => updateFormData('market', 'HK')}
                            >
                                港股
                            </Tag>
                            <Tag 
                                className={`stock-market-tag ${formData.market === 'US' ? 'is-active' : ''}`} 
                                onClick={() => updateFormData('market', 'US')}
                            >
                                美股
                            </Tag>
                        </div>
                    </section>
                    <section className="create-version-page__section">
                    <div className="create-version-page__section-title">核心投资策略</div>
                        <Input.TextArea
                            className='ant-edit-input'
                            rows={12}
                            placeholder="请输入本次版本的完整投资策略正文"
                            value={formData.strategy_snapshot}
                            onChange={(e) => {
                                updateFormData('strategy_snapshot', e.target.value)
                            }}
                        />
                    </section>

                    <section className="create-version-page__section">
                        <div className="create-version-page__section-title">关注板块</div>
                        <div className="create-version-page__tags flex">
                            {sectorOptions.map((sector) => (
                                <Tag.CheckableTag
                                    key={sector}
                                    checked={formData.focus_sectors.includes(sector)}
                                    onChange={(checked) => toggleSector(sector, checked)}
                                    className="border-tag text-11"
                                >
                                    {sector}
                                </Tag.CheckableTag>
                            ))}
                        </div>
                    </section>

                    <section className="create-version-page__section">
                        <div className="create-version-page__section-title">风险约束</div>
                        <div className="create-version-page__constraints">
                            {formData.constraints.map((item, index) => (
                                <div key={index} className="create-version-page__constraint-item flex flex-center">
                                    <span className="create-version-page__constraint-dot" />
                                    <Input
                                        value={item}
                                        onChange={(e) => updateConstraintDraft(e.target.value, index)}
                                        onBlur={(e) => changeConstraint(e.target.value, index)}
                                        placeholder="例如：单股持仓不超过20%、行业暴露不超过30%..."
                                    />
                                    <Button
                                        type="text"
                                        style={{ border: 'none' }}
                                        icon={<CloseOutlined />}
                                        onClick={() => removeConstraint(index)}
                                    />
                                </div>
                            ))}
                        </div>
                        <Button icon={<PlusOutlined />} style={{ marginTop: '14px' }} onClick={addConstraint}>
                            添加约束条件
                        </Button>
                    </section>

                    <div className="create-version-page__actions">
                        <Button
                            onClick={() => void handleSubmit('draft')}
                            loading={submitting}
                            disabled={submitting}
                        >
                            {isInPlaceEdit ? '💾 保存修改' : '💾 保存草稿'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<CaretRightOutlined />}
                            onClick={handleRunValidation}
                            loading={submitting}
                            disabled={submitting}
                        >
                            运行回测验证
                        </Button>
                        {!isInPlaceEdit && (
                            <Button
                                className="create-version-page__submit"
                                onClick={() => void handleSubmit('submit')}
                                loading={submitting}
                                disabled={submitting}
                            >
                                📤 提交审批
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
    return content
}
